import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudIcon,
  MudModal,
  MudInfoBox,
  MudTag,
  MudTooltip,
} from '@egov-moldova/mud-react';
import {
  ExpiryMonthInput,
  formatNumber,
  MetadataLine,
  onModalDismiss,
  useToast,
} from '@simsm/ui-shared';
import { apiErrorKey } from '../../../app/api-client';
import { requestCorrection } from '../../../features/corrections/api';
import { todayIso } from '../../../features/reporting/medicine-api';
import {
  authorisationStatus,
  medicineStateStatus,
} from '../../../features/reporting/medicine-identity';
import { CorrectableLotCombobox, type PickedLot } from './CorrectableLotCombobox';
import { CorrectionRequestReview } from './CorrectionRequestReview';
import '../reporting/expiry-month.css';
import '../reporting/stock-movement-dialog.css';

/** Why the request cannot be sent yet, in the order the reader fills the form. */
export type NewCorrectionBlocker = 'lot' | 'expiry' | 'expiry_unchanged' | null;

/**
 * caiet UC51-04 — "nu va admite inițierea corectării fără modificarea datei".
 * The rule is enforced by the service (`EXPIRY_UNCHANGED`); stating it here as
 * well is what lets the screen say so before the round trip, and lets the same
 * sentence appear under the field.
 *
 * spec 041 — the comparison stays exact, and that is the point. The proposal is
 * now entered as a month and reaches this function already completed to the
 * month's last day, so proposing March 2027 against a lot expiring 31.03.2027
 * is caught as unchanged. Against a lot expiring 17.03.2027 it is NOT: that
 * request moves the date by two weeks and is exactly the correction UC51 is
 * for. Comparing at month precision would have swallowed it.
 */
export function newCorrectionBlocker(input: {
  hasLot: boolean;
  currentExpiry: string;
  proposedExpiry: string;
}): NewCorrectionBlocker {
  if (!input.hasLot) return 'lot';
  if (input.proposedExpiry.length !== 10) return 'expiry';
  if (input.proposedExpiry === input.currentExpiry) return 'expiry_unchanged';
  return null;
}

/**
 * The wireframe's field label (SolicitariCorectare.tsx: `Medicament și lot *`
 * with its InfoTip): the label, the required mark, the (i) tip. The same markup
 * the reporting dialog's FieldHead draws, on its classes. AGE draws a label
 * inside the control's shadow root, where nothing can sit beside it, so the
 * label is drawn here and the control is named by `ariaLabel`.
 */
function FieldHead({ label, tip }: { label: string; tip: string }): JSX.Element {
  return (
    <span className="simsm-movement-dialog__field-head">
      <span className="simsm-movement-dialog__field-label">
        {label}
        <span className="simsm-movement-dialog__field-req" aria-hidden="true">
          {' '}
          *
        </span>
      </span>
      <MudTooltip content={tip} position="top">
        <span
          slot="trigger"
          className="simsm-movement-dialog__field-info"
          role="img"
          aria-label={tip}
        >
          <MudIcon name="circle-info" variant="filled" size={16} />
        </span>
      </MudTooltip>
    </span>
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * caiet UC51-01 — the request the dedicated menu initiates, drawn after the
 * wireframe's dialog (SolicitariCorectare.tsx): the lot found by search and then
 * shown as a summary, followed by the current and proposed expiry side by side.
 *
 * It asks for exactly what UC51 asks for: the lot and the new date. The
 * "Motivul corectării" field is gone (owner ruling 2026-09-15) — neither the
 * caiet (UC51-01..05, UC22) nor the wireframe names a reason, and core-service
 * no longer requires one.
 *
 * One departure from the wireframe, on purpose: the new date is a month and a
 * year, not the wireframe's date picker with "fără zi" (owner ruling 2026-09-06,
 * spec 041).
 *
 * The report-row shortcut (`pages/operator/reports/CorrectionExpiryModal`) stays
 * as it is. It is a different requirement — UC93-12, correcting a stock movement
 * — and the two are kept distinguishable rather than merged.
 */
export function NewCorrectionRequestModal({ open, onClose }: Props): JSX.Element {
  const { t } = useTranslate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [picked, setPicked] = useState<PickedLot | null>(null);
  const [proposedExpiry, setProposedExpiry] = useState('');
  // UC51-05: the form, then its explicit confirmation; only the second POSTs
  // (docs/shape-ops-expiry-correction-gate.md D5).
  const [step, setStep] = useState<'form' | 'review'>('form');
  // The key, not the sentence: a language switch while the refusal is on screen
  // must re-render it in the new language.
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const currentExpiry = picked?.lot.lotExpiry ?? '';
  const blocker = newCorrectionBlocker({
    hasLot: picked !== null,
    currentExpiry,
    proposedExpiry,
  });
  const medicineStatuses =
    picked?.medicine.catalogKnown === true
      ? [authorisationStatus(picked.medicine, todayIso()), medicineStateStatus(picked.medicine)]
      : [];

  function reset(): void {
    setPicked(null);
    setProposedExpiry('');
    setStep('form');
    setErrorKey(null);
  }

  function close(): void {
    reset();
    onClose();
  }

  const submit = useMutation({
    mutationFn: () =>
      requestCorrection({
        medicineCode: picked?.medicine.code ?? '',
        lot: picked?.lot.lot ?? '',
        proposedExpiry,
      }),
    onSuccess: () => {
      // caiet UC51-05: the request appears in the operator's list with status
      // "Spre aprobare" — so the list and the badge that counts it are both
      // stale the moment this returns.
      void queryClient.invalidateQueries({ queryKey: ['operator-corrections'] });
      void queryClient.invalidateQueries({ queryKey: ['my-correction-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['correctable-lots'] });
      void queryClient.invalidateQueries({ queryKey: ['correction-eligibility'] });
      toast({
        variant: 'success',
        title: t('correction_new.toast.submitted'),
        text: t('correction_new.toast.submitted_text', {
          medicine: picked?.medicine.name || picked?.medicine.code || '',
          lot: picked?.lot.lot || '',
        }),
      });
      close();
    },
    onError: (e: unknown) => setErrorKey(apiErrorKey(e, 'correction.error.generic')),
  });

  return (
    // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — seven AGE
    // components emit `mudClose` bubbling+composed, so a descendant closing inside this
    // modal reaches this handler too; the guard answers only a real dismissal.
    <MudModal
      open={open}
      size="md"
      closeLabel={t('common.close')}
      onMudClose={onModalDismiss(close)}
    >
      <h2 slot="title">
        {step === 'review' ? t('correction_confirm.title') : t('correction_new.title')}
      </h2>
      {step === 'review' && picked ? (
        <div className="simsm-correction-new">
          <CorrectionRequestReview
            medicine={picked.medicine.name || picked.medicine.code}
            lot={picked.lot.lot}
            currentExpiry={currentExpiry}
            currentExpiryPrecision={picked.lot.lotExpiryPrecision}
            proposedExpiry={proposedExpiry}
            proposedExpiryPrecision="MONTH"
          />
          {errorKey ? (
            <MudInfoBox
              emphasis="strong"
              role="alert"
              variant="warning"
              titleText={t('correction.error.title')}
            >
              {t(errorKey)}
            </MudInfoBox>
          ) : null}
        </div>
      ) : (
        <div className="simsm-correction-new">
          <div className="simsm-correction-new__field">
            <FieldHead label={t('correction_new.field.lot')} tip={t('correction_new.tip.lot')} />
            {picked ? (
              // The wireframe's `cor-panou`: the medicine with its lot and stock,
              // and "Schimbă" beside it. The expiry comparison has its own two
              // fields below, where the relationship is visible at a glance.
              <div className="simsm-correction-new__panel">
                <div className="simsm-correction-new__panel-top">
                  <span className="simsm-correction-new__panel-identity">
                    <span className="simsm-correction-new__panel-name-line">
                      <span className="simsm-correction-new__panel-name">
                        {picked.medicine.name || picked.medicine.code}
                      </span>
                      {medicineStatuses.length > 0 ? (
                        <span className="simsm-correction-new__panel-status">
                          {medicineStatuses.map((status) => (
                            <MudTag
                              key={status.key}
                              variant="status"
                              size="sm"
                              semantic={status.semantic}
                              label={t(status.key)}
                            />
                          ))}
                        </span>
                      ) : null}
                    </span>
                    <MetadataLine
                      wrap
                      items={[
                        {
                          label: t('operator_corrections.field.form'),
                          value: picked.medicine.pharmaceuticalForm,
                        },
                        {
                          label: t('operator_corrections.field.dose'),
                          value: picked.medicine.dose,
                        },
                        {
                          label: t('operator_corrections.field.division'),
                          value: picked.medicine.division,
                        },
                        {
                          label: t('operator_corrections.field.code'),
                          value: picked.medicine.code,
                        },
                        { label: t('operator_corrections.field.dci'), value: picked.medicine.dci },
                      ]}
                    />
                    <dl className="simsm-correction-new__panel-lot-facts">
                      <div>
                        <dt>{t('correction_new.meta.lot')}</dt>
                        <dd>{picked.lot.lot}</dd>
                      </div>
                      <div>
                        <dt>{t('correction_new.meta.stock')}</dt>
                        <dd>{formatNumber(Number(picked.lot.quantity))}</dd>
                      </div>
                    </dl>
                  </span>
                  <MudButton
                    shape="circular"
                    appearance="text"
                    variant="strict"
                    size="sm"
                    onClick={() => {
                      setPicked(null);
                      setProposedExpiry('');
                    }}
                  >
                    {t('correction_new.action.change')}
                  </MudButton>
                </div>
              </div>
            ) : (
              <CorrectableLotCombobox onPick={setPicked} autoFocus={open} />
            )}
          </div>

          {picked ? (
            <div className="simsm-correction-new__dates">
              <ExpiryMonthInput
                value={currentExpiry}
                onChange={() => undefined}
                label={t('correction_new.field.current_expiry')}
                ariaLabel={t('correction_new.field.current_expiry')}
                placeholder={t('expiry.month_placeholder')}
                readonly
              />
              <ExpiryMonthInput
                value={proposedExpiry}
                onChange={setProposedExpiry}
                invalid={blocker === 'expiry_unchanged'}
                label={t('correction_new.field.expiry')}
                ariaLabel={t('correction_new.field.expiry')}
                placeholder={t('expiry.month_placeholder')}
                errorText={
                  blocker === 'expiry_unchanged' ? t('correction_new.hint.same_date') : undefined
                }
                required
              />
            </div>
          ) : null}

          {errorKey ? (
            <MudInfoBox
              emphasis="strong"
              role="alert"
              variant="warning"
              titleText={t('correction.error.title')}
            >
              {t(errorKey)}
            </MudInfoBox>
          ) : null}
        </div>
      )}
      {step === 'review' ? (
        <div slot="actions" className="simsm-correction-new__footer">
          <MudButton
            shape="circular"
            appearance="outlined"
            variant="strict"
            size="md"
            disabled={submit.isPending}
            onClick={() => {
              setErrorKey(null);
              setStep('form');
            }}
          >
            {t('correction_confirm.action.back')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            disabled={blocker !== null || submit.isPending}
            onClick={() => submit.mutate()}
          >
            {t('correction_confirm.action.confirm')}
          </MudButton>
        </div>
      ) : (
        <div slot="actions" className="simsm-correction-new__footer">
          <MudButton
            shape="circular"
            appearance="outlined"
            variant="strict"
            size="md"
            onClick={close}
          >
            {t('correction_new.action.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            disabled={blocker !== null}
            onClick={() => {
              setErrorKey(null);
              setStep('review');
            }}
          >
            {t('correction_new.action.submit')}
          </MudButton>
        </div>
      )}
    </MudModal>
  );
}
